import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZone,
  ContainerEntity,
  Dispatch,
  DispatchEntity,
  dispatchStatus,
  getObjectId,
  Quantity,
  transformDispatch,
  TransportAssocEntity,
  useCreateDispatchMutation,
  useGetDispatchQuery,
  useUpdateDispatchMutation,
  useGetDispatchCarryoverPolicyQuery,
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {v4 as uuid} from "uuid";
import {notifications} from "@mantine/notifications";
import {DispatchForm, DispatchFormOptions, DispatchFormValues, ThemedPage, useApp, useAuth} from "@ots-frontend/ots-ui";

type Props = {
  id?: string;
  copy?: boolean;
  options?: DispatchFormOptions
  formDefaults?: Partial<DispatchFormValues>
}

export const DispatchFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const options = state?.options
  const formDefaults = state?.formDefaults;
  return <DispatchFormPage id={id} copy={copy} options={options} formDefaults={formDefaults}/>;
};

export const toFormDefaultsFromDispatch = (dispatch: DispatchEntity | undefined): Partial<DispatchFormValues> => {
  let quantity: Quantity | undefined = undefined;

  if (dispatch && dispatch.quantity && dispatch.quantityUom) {
    quantity = {
      number: dispatch.quantity,
      units: dispatch.quantityUom
    };
  }
  return {
    date: adjustFromTimeZoneSafely(dispatch?.scheduleDttm),
    company: dispatch?.personCompanyId ?? dispatch?.trailerCompanyId,
    person: dispatch?.personId,
    vehicle: dispatch?.vehicleId,
    trailer: dispatch?.trailerId,
    quantity: quantity,
    freightType: dispatch?.freightTypeId,
    serviceType: dispatch?.pickupServiceTypeId,
    pickup: {
      site: dispatch?.pickupSiteId,
      well: dispatch?.pickupWellId,
      container: dispatch?.pickupContainerId,
    },
    delivery: {
      site: dispatch?.deliverySiteId,
      well: dispatch?.deliveryWellId,
      container: dispatch?.deliveryContainerId,
    },
    workOrderNumber: dispatch?.workOrderNumber,
    carryoverExcluded: dispatch?.carryoverExcluded,
    comments: undefined
  };
};

export const toFormDefaultsFromTta = (tta: TransportAssocEntity | undefined): Partial<DispatchFormValues> => {
  let quantity: Quantity | undefined = undefined;

  const pickup = tta?.pickupTransport;
  const delivery = tta?.deliveryTransport;

  if (pickup && pickup.quantityDecimal && pickup.quantityUnitOfMeasure) {
    quantity = {
      number: pickup.quantityDecimal,
      units: pickup.quantityUnitOfMeasure
    };
  }
  return {
    date: adjustFromTimeZoneSafely(pickup?.createDttm),
    company: pickup?.trailerCompanyId ?? delivery?.trailerCompanyId,
    person: pickup?.personId,
    vehicle: pickup?.vehicleId,
    trailer: pickup?.trailerId,
    quantity: quantity,
    freightType: pickup?.waterTypeId,
    serviceType: pickup?.serviceTypeId,
    pickup: {
      site: pickup?.siteId,
      well: pickup?.wellId,
      container: pickup?.containerId,
    },
    delivery: {
      site: delivery?.siteId,
      well: delivery?.wellId,
      container: delivery?.containerId,
    },
    workOrderNumber: pickup?.workOrderNumber ?? delivery?.workOrderNumber,
    comments: undefined
  };
};

export const toFormDefaultsFromContainer = (container: ContainerEntity | undefined): Partial<DispatchFormValues> => {
  return {
    freightType: container?.waterTypeId,
    pickup: {
      site: container?.siteId,
      container: container?.id
    },
  };
};

export const DispatchFormPage = (props: Props) => {
  const {id, copy = false, options, formDefaults} = props;

  const navigate = useNavigate();
  const {userId} = useAuth()
  const {regionId, appCategory} = useApp()
  const {t} = useTranslation(["translation"]);

  const {currDispatch, isLoading} = useGetDispatchQuery(id ?? skipToken, {
    selectFromResult: ({data, isLoading}) => ({
      currDispatch: data ? transformDispatch(data) : undefined,
      isLoading: isLoading
    })
  });

  const policyRegionId = options?.regionId ?? currDispatch?.regionId ?? regionId;
  const {data: carryoverPolicy, isLoading: isPolicyLoading} = useGetDispatchCarryoverPolicyQuery(policyRegionId ? {
    regionId: policyRegionId,
    personId: currDispatch?.personId,
    category: (options?.category ?? currDispatch?.category ?? appCategory)?.code,
  } : skipToken);

  const _formDefaults: Partial<DispatchFormValues> = useMemo(() => {
    const defaults = formDefaults ? formDefaults : toFormDefaultsFromDispatch(currDispatch);
    // a new dispatch starts from the region/company default; an existing one keeps its own choice
    return {
      ...defaults,
      carryoverExcluded: defaults.carryoverExcluded ?? carryoverPolicy?.excludedByDefault ?? false,
    };
  }, [carryoverPolicy, currDispatch, formDefaults]);

  const titleKey: string = useMemo(() => {
    if (currDispatch && !copy) {
      return "page.dispatch.form.title.edit";
    } else {
      return "page.dispatch.form.title.new";
    }
  }, [currDispatch, copy]);

  const [createDispatch, {isLoading: isCreating}] = useCreateDispatchMutation();
  const [updateDispatch, {isLoading: isUpdating}] = useUpdateDispatchMutation();

  const onDispatchSubmit = useCallback((formValues: DispatchFormValues) => {

    const patch: Partial<Dispatch> = {
      scheduleDttm: adjustToTimeZone(formValues.date).getTime(),
      personId: getObjectId(formValues.person),
      trailerId: getObjectId(formValues.trailer),
      vehicleId: getObjectId(formValues.vehicle),
      quantityDecimal: formValues.quantity.number,
      quantityUnitOfMeasure: formValues.quantity.units,
      waterTypeId: getObjectId(formValues.freightType),
      pickupServiceTypeId: getObjectId(formValues.serviceType),
      deliveryServiceTypeId: getObjectId(formValues.serviceType),
      pickupSiteId: getObjectId(formValues.pickup?.site),
      pickupWellId: getObjectId(formValues.pickup?.well),
      pickupContainerId: getObjectId(formValues.pickup?.container),
      deliverySiteId: getObjectId(formValues.delivery?.site),
      deliveryWellId: getObjectId(formValues.delivery?.well),
      deliveryContainerId: getObjectId(formValues.delivery?.container),
      workOrderNumber: formValues.workOrderNumber,
      carryoverExcluded: formValues.carryoverExcluded ?? false,
    }

    if (currDispatch && !copy) {
      const dispatch: Partial<Dispatch> = {
        ...currDispatch.asTypeValue(),
        ...patch,
        authentication: userId,
        lastModifiedDttm: adjustToTimeZone().getTime(),
        comments: formValues.comments,
      };
      updateDispatch(dispatch)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Dispatch Updated",
            message: "The driver will be notified of the updated job."
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    } else {

      const splits: Partial<Dispatch>[] | undefined = formValues.splits?.map((splitValues) => ({
        quantityDecimal: splitValues.quantity?.number,
        quantityUnitOfMeasure: splitValues.quantity?.units,
        waterTypeId: getObjectId(splitValues.freightType),
        pickupServiceTypeId: getObjectId(splitValues.serviceType),
        deliveryServiceTypeId: getObjectId(splitValues.serviceType),
        pickupSiteId: splitValues.type === "PICKUP" ? getObjectId(splitValues.location?.site) : undefined,
        pickupWellId: splitValues.type === "PICKUP" ? getObjectId(splitValues.location?.well) : undefined,
        pickupContainerId: splitValues.type === "PICKUP" ? getObjectId(splitValues.location?.container) : undefined,
        deliverySiteId: splitValues.type === "DELIVERY" ? getObjectId(splitValues.location?.site) : undefined,
        deliveryWellId: splitValues.type === "DELIVERY" ? getObjectId(splitValues.location?.well) : undefined,
        deliveryContainerId: splitValues.type === "DELIVERY" ? getObjectId(splitValues.location?.container) : undefined,
        comments: splitValues.comments,
      }))

      const dispatch: Partial<Dispatch> = {
        ...patch,
        category: formValues.category?.code,
        deviceId: "0",
        authentication: userId,
        id: uuid(),
        type: "PICKUP",
        regionId: regionId,
        status: dispatchStatus.pending.code,
        comments: formValues.comments,
        createdById: userId,
        splits: splits,
      };

      createDispatch(dispatch)
        .unwrap()
        .then(() => {
          notifications.show({
            color: "green",
            title: "Dispatch Created",
            message: "The driver will be notified of the new job."
          });
          navigate(-1);
        })
        .catch((rejected) =>
          notifications.show({
            color: "red",
            title: "Error",
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"})
          })
        );
    }
  }, [currDispatch, copy, userId, updateDispatch, navigate, t, regionId, createDispatch]);

  return (
    <ThemedPage>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {(isLoading || (!id && isPolicyLoading)) && <Loader/>}
            {!isLoading && !(!id && isPolicyLoading) && (
              <Paper miw={1000} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {t(titleKey)}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <DispatchForm
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onDispatchSubmit}
                  options={{
                    ...options,
                    regionId: options?.regionId ?? currDispatch?.regionId ?? regionId,
                    category: options?.category ?? currDispatch?.category ?? appCategory,
                    categoryDisabled: (!copy && typeof currDispatch !== 'undefined') || options?.categoryDisabled,
                    splitsDisabled: (!copy && typeof currDispatch !== 'undefined') || options?.splitsDisabled,
                    crudeOilFreightTypeDefaultEnabled: copy || typeof currDispatch === 'undefined',
                    carryoverPolicy: carryoverPolicy,
                  }}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ThemedPage>
  );
};
